import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useDisputesStore } from '../../stores/disputesStore';
import { DisputeCard } from './DisputeCard';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { useLanguage } from '../../contexts/LanguageContext';
import { ChevronDown, ChevronUp, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

// Number of disputes to show per page
const ITEMS_PER_PAGE = 10;

export const DisputesList = () => {
  const { 
    disputes = {}, 
    loading, 
    error, 
    fetchDisputes 
  } = useDisputesStore();
  const { translations } = useLanguage();
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    selectedOrganizer: '',
    timeRange: 'all', // 'all', '24h', '3d', '7d'
    packageName: '', // Added package filter
  });
  const [isStatsExpanded, setIsStatsExpanded] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Calculate time thresholds
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000;
  const threeDays = 3 * twentyFourHours;
  const sevenDays = 7 * twentyFourHours;

  // Filter and sort disputes
  const filteredDisputes = React.useMemo(() => {
    return Object.values(disputes)
      .filter(dispute => {
        const matchesSearch = !filters.search || 
          dispute.pilgrimName.toLowerCase().includes(filters.search.toLowerCase()) ||
          dispute.passportNumber.toLowerCase().includes(filters.search.toLowerCase());

        const matchesStatus = !filters.status || dispute.status === filters.status;
        
        const matchesOrganizer = !filters.selectedOrganizer || 
          dispute.disputingGuides.some(guide => guide.organizerName === filters.selectedOrganizer);

        const matchesPackage = !filters.packageName || 
          dispute.packageName === filters.packageName;

        let matchesTimeRange = true;
        if (filters.timeRange !== 'all') {
          const timeDiff = now - dispute.createdAt;
          switch (filters.timeRange) {
            case '24h':
              matchesTimeRange = timeDiff <= twentyFourHours;
              break;
            case '3d':
              matchesTimeRange = timeDiff <= threeDays;
              break;
            case '7d':
              matchesTimeRange = timeDiff <= sevenDays;
              break;
          }
        }

        return matchesSearch && matchesStatus && matchesOrganizer && matchesTimeRange && matchesPackage;
      })
      .sort((a, b) => b.createdAt - a.createdAt); // Sort by creation time, newest first
  }, [disputes, filters, now]);

  // Get unique organizers
  const organizers = React.useMemo(() => {
    return Array.from(new Set(
      Object.values(disputes).flatMap(dispute => 
        dispute.disputingGuides.map(guide => guide.organizerName)
      )
    )).sort();
  }, [disputes]);

  // Get unique packages
  const packages = React.useMemo(() => {
    return Array.from(new Set(
      Object.values(disputes)
        .map(dispute => dispute.packageName)
        .filter(Boolean) // Filter out undefined/null values
    )).sort();
  }, [disputes]);

  // Calculate stats
  const stats = React.useMemo(() => {
    const disputesList = Object.values(disputes);
    const totalDisputes = disputesList.length;
    const pendingDisputes = disputesList.filter(d => d.status === 'pending').length;
    const resolvedDisputes = disputesList.filter(d => d.status === 'resolved').length;
    const processingDisputes = disputesList.filter(d => d.status === 'under_processing').length;

    // Calculate disputes by organizer
    const organizerStats = disputesList.reduce((acc, dispute) => {
      dispute.disputingGuides.forEach(guide => {
        if (guide.organizerName) {
          acc[guide.organizerName] = (acc[guide.organizerName] || 0) + 1;
        }
      });
      return acc;
    }, {} as Record<string, number>);

    return {
      total: totalDisputes,
      pending: pendingDisputes,
      resolved: resolvedDisputes,
      processing: processingDisputes,
      byOrganizer: organizerStats
    };
  }, [disputes]);

  // Check if any filters are active
  const hasActiveFilters = filters.search || filters.status || filters.selectedOrganizer || filters.timeRange !== 'all' || filters.packageName;

  // Pagination
  const totalPages = Math.ceil(filteredDisputes.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedDisputes = filteredDisputes.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Statistics Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {translations?.disputesList?.statistics || 'Dispute Statistics'}
          </h2>
          <button
            onClick={() => setIsStatsExpanded(!isStatsExpanded)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            {isStatsExpanded ? (
              <ChevronUp className="text-gray-500" />
            ) : (
              <ChevronDown className="text-gray-500" />
            )}
          </button>
        </div>

        {isStatsExpanded && (
          <div className="space-y-6">
            {/* Overall Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-primary-50 p-4 rounded-lg">
                <div className="text-sm text-primary-600">Total Disputes</div>
                <div className="text-2xl font-bold text-primary-700">{stats.total}</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-sm text-yellow-600">Pending</div>
                <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-blue-600">Processing</div>
                <div className="text-2xl font-bold text-blue-700">{stats.processing}</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-green-600">Resolved</div>
                <div className="text-2xl font-bold text-green-700">{stats.resolved}</div>
              </div>
            </div>

            {/* Organizer Stats */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                {translations?.disputesList?.disputesByOrganizer || 'Disputes by Organizer'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(stats.byOrganizer).map(([organizer, count]) => (
                  <div key={organizer} className="bg-gray-50 p-4 rounded-lg">
                    <div className="font-medium">{organizer}</div>
                    <div className="text-lg text-gray-700">{count} disputes</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <input
            type="text"
            placeholder={translations?.disputesList?.searchPlaceholder || 'Search by name or passport...'}
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          />

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="under_processing">Processing</option>
            <option value="resolved">Resolved</option>
          </select>

          {/* Organizer Filter */}
          <select
            value={filters.selectedOrganizer}
            onChange={(e) => setFilters(prev => ({ ...prev, selectedOrganizer: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Organizers</option>
            {organizers.map(org => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>

          {/* Package Filter */}
          <select
            value={filters.packageName}
            onChange={(e) => setFilters(prev => ({ ...prev, packageName: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Packages</option>
            {packages.map(pkg => (
              <option key={pkg} value={pkg}>{pkg}</option>
            ))}
          </select>

          {/* Time Range Filter */}
          <select
            value={filters.timeRange}
            onChange={(e) => setFilters(prev => ({ ...prev, timeRange: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Time</option>
            <option value="24h">Last 24 Hours</option>
            <option value="3d">Last 3 Days</option>
            <option value="7d">Last 7 Days</option>
          </select>
        </div>

        {/* Active Filters and Results Count */}
        {hasActiveFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-primary-500" />
              <span className="text-sm text-gray-600">
                {translations?.disputesList?.activeFilters || 'Active Filters:'}
              </span>
              <div className="flex flex-wrap gap-2">
                {filters.search && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.disputesList?.search || 'Search'}: {filters.search}
                  </span>
                )}
                {filters.status && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.disputesList?.status || 'Status'}: {filters.status}
                  </span>
                )}
                {filters.selectedOrganizer && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.disputesList?.organizer || 'Organizer'}: {filters.selectedOrganizer}
                  </span>
                )}
                {filters.packageName && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.disputesList?.package || 'Package'}: {filters.packageName}
                  </span>
                )}
                {filters.timeRange !== 'all' && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.disputesList?.timeRange || 'Time Range'}: {
                      filters.timeRange === '24h' ? 'Last 24 Hours' :
                      filters.timeRange === '3d' ? 'Last 3 Days' :
                      'Last 7 Days'
                    }
                  </span>
                )}
              </div>
            </div>
            <div className="text-sm font-medium text-gray-700">
              {translations?.disputesList?.resultsCount || 'Showing'}{' '}
              <span className="text-primary-600">{filteredDisputes.length}</span>{' '}
              {translations?.disputesList?.resultsOf || 'results out of'}{' '}
              <span className="text-gray-900">{stats.total}</span>
            </div>
          </div>
        )}
      </div>

      {/* Disputes List */}
      <div className="space-y-4">
        {paginatedDisputes.map((dispute) => (
          <DisputeCard key={dispute.id} dispute={dispute} />
        ))}

        {filteredDisputes.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">
              {translations?.disputesList?.noDisputes || 'No disputes match the selected filters'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredDisputes.length > 0 && (
        <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={20} />
            </button>
            
            <div className="flex items-center gap-1">
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show first page, last page, current page, and pages around current
                let pageToShow;
                if (totalPages <= 5) {
                  // If 5 or fewer pages, show all
                  pageToShow = i + 1;
                } else if (currentPage <= 3) {
                  // If near start, show first 5 pages
                  pageToShow = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // If near end, show last 5 pages
                  pageToShow = totalPages - 4 + i;
                } else {
                  // Otherwise show current page and 2 pages on each side
                  pageToShow = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageToShow}
                    onClick={() => setCurrentPage(pageToShow)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                      currentPage === pageToShow
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {pageToShow}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="text-sm text-gray-500">
            {translations?.pagination?.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredDisputes.length)} {translations?.pagination?.of || 'of'} {filteredDisputes.length}
          </div>
        </div>
      )}
    </div>
  );
};